import React, { Suspense, type JSX } from "react"
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical"

const ObjectLinkComponent = React.lazy(() => import("./component"))

export interface ObjectLinkData {
  type: string
  id: string
  label: string
}

type SerializedObjectLinkNode = Spread<{ objectType: string; id: string; label: string }, SerializedLexicalNode>

export class ObjectLinkNode extends DecoratorNode<JSX.Element> {
  __objectType: string
  __objectId: string
  __label: string

  static getType(): string {
    return "object-link"
  }

  static clone(node: ObjectLinkNode): ObjectLinkNode {
    return new ObjectLinkNode(node.__objectType, node.__objectId, node.__label, node.__key)
  }

  constructor(objectType: string, objectId: string, label: string, key?: NodeKey) {
    super(key)
    this.__objectType = objectType
    this.__objectId = objectId
    this.__label = label
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span")
    element.textContent = this.__label
    return { element }
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span")
    span.style.display = "inline"
    return span
  }

  updateDOM(): false {
    return false
  }

  isInline(): boolean {
    return true
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={<span>{this.__label}</span>}>
        <ObjectLinkComponent objectType={this.__objectType} objectId={this.__objectId} label={this.__label} />
      </Suspense>
    )
  }

  getTextContent(): string {
    return this.__label
  }

  exportJSON(): SerializedObjectLinkNode {
    return {
      ...super.exportJSON(),
      type: "object-link",
      objectType: this.__objectType,
      id: this.__objectId,
      label: this.__label,
      version: 1,
    }
  }

  static importJSON(serializedNode: SerializedObjectLinkNode): ObjectLinkNode {
    return $createObjectLinkNode(serializedNode.objectType, serializedNode.id, serializedNode.label)
  }
}

export function $createObjectLinkNode(objectType: string, objectId: string, label: string): ObjectLinkNode {
  return $applyNodeReplacement(new ObjectLinkNode(objectType, objectId, label))
}

export function $isObjectLinkNode(node: LexicalNode | null | undefined): node is ObjectLinkNode {
  return node instanceof ObjectLinkNode
}
